import { z } from 'zod'

/** The categories in the order the app offers them (mirrors internal/kinds/date). */
export const DATE_CATEGORIES = [
  'birthday',
  'anniversary',
  'appointment',
  'deadline',
  'bill',
  'countdown',
  'other',
] as const
export type DateCategory = (typeof DATE_CATEGORIES)[number]

/**
 * How many days ahead each category reminds before Settings `date_leads` says
 * otherwise: a week for a card or a present, a few days to pay or prepare,
 * the day before an appointment, nothing ahead of a countdown.
 */
export const DEFAULT_LEADS: Record<DateCategory, number> = {
  birthday: 7,
  anniversary: 7,
  appointment: 1,
  deadline: 3,
  bill: 3,
  countdown: 0,
  other: 1,
}

/**
 * A Date is a day to remember that is not a thing to do: a birthday, an
 * appointment, a renewal, a countdown. `date` is the first (or only)
 * occurrence; a yearly one keeps the original day so an age can be said.
 */
export const dateSpec = z
  .object({
    title: z.string().min(1).max(200),
    category: z.enum(DATE_CATEGORIES),
    /** local calendar date, YYYY-MM-DD */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** local HH:MM; absent is all day */
    time: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
      .optional(),
    repeat: z.enum(['none', 'weekly', 'fortnightly', 'monthly', 'yearly']).optional(),
    /** a yearly date whose year is not known: no age, no count of years */
    no_year: z.boolean().optional(),
    /** overrides the category's lead; 0 is none */
    lead_days: z.number().int().min(0).max(90).optional(),
    notes: z.string().max(2000).optional(),
    icon: z.string().max(64).optional(),
    /** an appointment: how long, where, and how long it takes to get there */
    minutes: z.number().int().min(5).max(1440).optional(),
    place: z.string().max(200).optional(),
    travel_minutes: z.number().int().min(0).max(600).optional(),
    /** the last day a repeat may fall on, and the occurrences that are off */
    until: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    skip: z
      .array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/))
      .max(200)
      .optional(),
  })
  .strict()
export type DateSpec = z.infer<typeof dateSpec>

/** Which occurrence each reminder last fired for; written by the server only. */
export const dateStatus = z
  .object({
    lead_fired_for: z.string().optional(),
    day_fired_for: z.string().optional(),
    ready_fired_for: z.string().optional(),
    leave_fired_for: z.string().optional(),
  })
  .passthrough()
export type DateStatus = z.infer<typeof dateStatus>
